import { SelectChangeEvent } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { ReactNode, useEffect, useMemo } from 'react';

import { DropdownListItem } from '@verantos-dev/shared-components';

import { useAppDispatch, useAppSelector } from '../../lib';
import {
  useGetHolidaysDataQuery,
  useGetIRRFilterTypesQuery,
  useGetIRRScoreDateRangeQuery,
  useGetMetricBatchTypeListQuery,
} from './api/metricsApiSlice';
import { setIRRFilterTypeId, setIRRScoreBatchTypeId, setIRRScoreDate } from './state/metricsSlice';
import {
  selectIRRFilterTypeId,
  selectIRRScoreBatchTypeId,
  selectIRRScoreDate,
  selectSelectedProjectId,
} from './state/selector';
import { IRRScoreDateRange } from './types/responseTypes';

type DropdownChangeHandler = (
  event: SelectChangeEvent<unknown> | (Event & { target: { value: unknown; name: string } }),
  child: ReactNode
) => void;

interface UseMetricIRRScoreResults {
  irrScoreDateRange: IRRScoreDateRange | undefined;
  isIRRScoreDateRangeLoading: boolean;
  date: Dayjs | null;
  handleChangeDate: (val: Dayjs | null) => void;
  shouldDisableDate: (day: Dayjs) => boolean;
  batchTypeOptions: DropdownListItem[];
  filterTypeOptions: DropdownListItem[];
  batchTypeId: string | null;
  filterTypeId: string | null;
  isMetricsBatchTypeListLoading: boolean;
  isFilterTypeListLoading: boolean;
  handleBatchTypeChange: DropdownChangeHandler;
  handleFilterTypeChange: DropdownChangeHandler;
  projectId: string | null;
}

export const useMetricIRRScore = (): UseMetricIRRScoreResults => {
  const projectId = useAppSelector(selectSelectedProjectId);
  const batchTypeId = useAppSelector(selectIRRScoreBatchTypeId);
  const date = useAppSelector(selectIRRScoreDate);
  const filterTypeId = useAppSelector(selectIRRFilterTypeId);
  const {
    data: irrScoreDateRangeData,
    isLoading: isIRRScoreDateRangeLoading,
    isFetching: isIRRScoreDateRangeFetching,
  } = useGetIRRScoreDateRangeQuery(
    {
      projectId: projectId || '',
      batchTypeId: batchTypeId || '',
    },
    {
      skip: !(projectId && batchTypeId),
      refetchOnMountOrArgChange: true,
    }
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (isIRRScoreDateRangeFetching) return;
    if (!irrScoreDateRangeData?.data?.minDate || !irrScoreDateRangeData?.data?.maxDate) return;
    dispatch(setIRRScoreDate(dayjs(irrScoreDateRangeData.data?.maxDate).toISOString()));
  }, [
    irrScoreDateRangeData?.data?.minDate,
    irrScoreDateRangeData?.data?.maxDate,
    isIRRScoreDateRangeFetching,
    dispatch,
  ]);

  const handleChangeDate = (val: Dayjs | null) => {
    if (val) {
      dispatch(setIRRScoreDate(val.toISOString()));
    }
  };

  const {
    data: metricsBatchTypeListData,
    isLoading: isMetricsBatchTypeListLoading,
    isFetching: isMetricsBatchTypeListFetching,
  } = useGetMetricBatchTypeListQuery({ projectId: projectId ?? '' }, { skip: !projectId });

  const batchTypeOptions: DropdownListItem[] = useMemo(() => {
    if (!metricsBatchTypeListData?.data || metricsBatchTypeListData.data.length === 0) {
      return [];
    }
    return metricsBatchTypeListData.data.map((batchType) => ({
      value: String(batchType.id),
      displayValue: batchType.label,
    }));
  }, [metricsBatchTypeListData]);

  const handleBatchTypeChange: DropdownChangeHandler = (event) => {
    const value = event.target?.value;
    if (typeof value === 'string') {
      dispatch(setIRRScoreBatchTypeId(value));
    }
  };

  const {
    data: irrFilterTypeListData,
    isLoading: isIRRFilterTypeListLoading,
    isFetching: isIRRFilterTypeListFetching,
  } = useGetIRRFilterTypesQuery();

  const filterTypeOptions: DropdownListItem[] = useMemo(() => {
    if (!irrFilterTypeListData?.data || irrFilterTypeListData.data.length === 0) {
      return [];
    }
    return irrFilterTypeListData.data.map((filterType) => ({
      value: String(filterType.id),
      displayValue: filterType.label,
    }));
  }, [irrFilterTypeListData]);

  useEffect(() => {
    if (!filterTypeOptions.length) return;

    if (filterTypeId) return;

    dispatch(setIRRFilterTypeId(filterTypeOptions[0].value));
  }, [filterTypeOptions, filterTypeId, dispatch]);

  const handleFilterTypeChange: DropdownChangeHandler = (event) => {
    const value = event.target?.value;
    if (typeof value === 'string') {
      dispatch(setIRRFilterTypeId(value));
    }
  };

  const { data: holidaysData } = useGetHolidaysDataQuery();

  const holidayDates = useMemo<Set<string>>(() => {
    if (!holidaysData?.data) return new Set();
    return new Set(holidaysData.data.map((h) => dayjs(h.date).format('YYYY-MM-DD')));
  }, [holidaysData]);

  const shouldDisableDate = (day: Dayjs) => {
    const dow = day.day();
    if (dow === 0 || dow === 6) return true;
    return holidayDates.has(day.format('YYYY-MM-DD'));
  };

  return {
    irrScoreDateRange: irrScoreDateRangeData?.data,
    isIRRScoreDateRangeLoading: isIRRScoreDateRangeLoading || isIRRScoreDateRangeFetching,
    date: date ? dayjs(date) : null,

    handleChangeDate,
    shouldDisableDate,
    batchTypeOptions,
    batchTypeId,
    isMetricsBatchTypeListLoading: isMetricsBatchTypeListFetching || isMetricsBatchTypeListLoading,
    isFilterTypeListLoading: isIRRFilterTypeListFetching || isIRRFilterTypeListLoading,
    handleBatchTypeChange,
    projectId,
    filterTypeOptions,
    filterTypeId,
    handleFilterTypeChange,
  };
};
